import { useRef } from 'react';
import { ArrowUpIcon, ArrowDownIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { TableColumn, SortOrder } from '@/types/table';

interface TableHeaderProps<TData> {
    columns: TableColumn<TData>[];
    sortBy?: string;
    sortOrder: SortOrder;
    onSortChange?: (sortBy: string, sortOrder: SortOrder) => void;
    columnWidths?: Record<string, number>;
    onStartResize?: (
        columnId: string,
        startWidthPx: number,
        event: React.PointerEvent<HTMLElement>,
    ) => void;
}

function getColumnId<TData>(column: TableColumn<TData>, index: number): string {
    return column.id ?? String(column.accessor ?? `col-${index}`);
}

export function TableHeader<TData>({
    columns,
    sortBy,
    sortOrder,
    onSortChange,
    columnWidths,
    onStartResize,
}: TableHeaderProps<TData>) {
    const tableHeaderRefs = useRef<Map<string, HTMLTableCellElement | null>>(
        new Map(),
    );

    const handleSort = (columnId: string) => {
        if (!onSortChange) return;
        const newOrder =
            sortBy === columnId && sortOrder === SortOrder.ASC
                ? SortOrder.DESC
                : SortOrder.ASC;
        onSortChange(columnId, newOrder);
    };
    return (
        <thead className="border-b border-grey-500 sticky top-0 bg-grey-600 z-10">
            <tr>
                {columns.map((column, i) => {
                    const isSortable =
                        column.enableSorting !== false &&
                        onSortChange &&
                        column.accessor;
                    const sortKey = String(column.accessor ?? column.id);
                    const isCurrentSort = sortBy === sortKey;
                    const isAscending =
                        isCurrentSort && sortOrder === SortOrder.ASC;
                    const colId = getColumnId(column, i);
                    const overrideWidth = columnWidths?.[colId];

                    const style: React.CSSProperties = {};
                    if (overrideWidth !== undefined) {
                        style.width = `${overrideWidth}px`;
                    } else if (column.width) {
                        style.width = column.width;
                    }
                    if (column.minWidth) style.minWidth = column.minWidth;
                    if (column.maxWidth) style.maxWidth = column.maxWidth;

                    const SortIcon = isAscending ? ArrowUpIcon : ArrowDownIcon;
                    const isInteractive = column.interactive;

                    return (
                        <th
                            key={i}
                            ref={(el) => {
                                tableHeaderRefs.current.set(colId, el);
                            }}
                            style={style}
                            className="h-12 px-4 py-2 text-left align-middle text-[14px] leading-5 font-medium text-white-100 relative"
                        >
                            {isSortable ? (
                                <button
                                    onClick={() => handleSort(sortKey)}
                                    className="flex items-center gap-1.5 transition-colors"
                                >
                                    {column.header}
                                    <SortIcon
                                        className={cn(
                                            'w-4 h-4 text-grey-300',
                                            isCurrentSort
                                                ? 'opacity-100'
                                                : 'opacity-50',
                                        )}
                                    />
                                </button>
                            ) : (
                                column.header
                            )}
                            {onStartResize && !isInteractive && (
                                <span
                                    onPointerDown={(e) => {
                                        const th =
                                            tableHeaderRefs.current.get(colId);
                                        const currentWidth =
                                            th?.getBoundingClientRect().width ??
                                            0;
                                        onStartResize(colId, currentWidth, e);
                                    }}
                                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none touch-none hover:bg-brand/40 active:bg-brand/60"
                                    aria-hidden
                                />
                            )}
                        </th>
                    );
                })}
            </tr>
        </thead>
    );
}
